import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/AuthCard";
import { FormField } from "@/components/auth/FormField";
import { PasswordField } from "@/components/auth/PasswordField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const result = await signIn(values.email.trim().toLowerCase(), values.password);
      if (result.nextStep?.signInStep === "CONFIRM_SIGN_UP") {
        toast.message("Please verify your email to continue.");
        navigate(`/auth/verify?email=${encodeURIComponent(values.email)}`);
        return;
      }
      if (result.isSignedIn) {
        navigate(next, { replace: true });
        return;
      }
      // Other challenges (MFA, etc.) - fall through with a soft prompt.
      toast.info("Additional verification required.");
    } catch (err) {
      const message = (err as { message?: string }).message || "Sign in failed.";
      if (message.toLowerCase().includes("not confirmed")) {
        navigate(`/auth/verify?email=${encodeURIComponent(values.email)}`);
        return;
      }
      setError("password", { message });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <AuthCard
      title="Welcome back"
      description="Sign in to continue learning."
      footer={
        <>
          Don't have an account?{" "}
          <Link to="/auth/otp" className="font-medium text-brand hover:underline">
            Continue with email
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField id="email" label="Email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoFocus
            placeholder="you@example.com"
            aria-invalid={!!errors.email}
            {...register("email")}
          />
        </FormField>

        <FormField id="password" label="Password" error={errors.password?.message}>
          <PasswordField
            id="password"
            autoComplete="current-password"
            placeholder="Your password"
            aria-invalid={!!errors.password}
            {...register("password")}
          />
        </FormField>

        <div className="flex justify-end">
          <Link to="/auth/forgot" className="text-sm text-brand hover:underline">
            Forgot password?
          </Link>
        </div>

        <Button type="submit" fullWidth loading={submitting}>
          Sign in
        </Button>

        <div className="relative my-2 flex items-center text-xs uppercase text-text-subtle">
          <div className="flex-1 border-t border-border" />
          <span className="px-3">or</span>
          <div className="flex-1 border-t border-border" />
        </div>

        <Button type="button" variant="secondary" fullWidth asChild>
          <Link to="/auth/otp">Continue with email</Link>
        </Button>
      </form>
    </AuthCard>
  );
}
